/**
 * 1.3.0 Administration → Media portal → Delivery specs (P1): the house formats a delivery is checked against: the QC
 * profile, accepted types and size, the metadata the supplier fills in, default extra requested items, the naming
 * rule, the acceptance rule and the reviewer.
 */
import { useState } from 'react';
import { ClipboardList, Plus, Trash2 } from 'lucide-react';
import { ITEM_KIND_PRESET, ITEM_KINDS, ITEM_TYPE_PRESETS, type AcceptanceMode, type DeliveryItemTemplate, type DeliverySpecDTO, type ItemKind, type ReviewerRef } from '@prism/contracts';
import { api, errorDetail } from '../../../api/client';
import { useDeliverySpecs, usePortalAdminRefresh, usePortalChoices, usePortalSettings } from '../../../api/hooks/portalAdmin';
import { Modal } from '../../../components/Modal';
import { TableScroll } from '../../../components/TableScroll';
import { toast } from '../../../components/Toasts';
import { ACCEPTANCE_LABEL, AcceptanceOptions, FieldPicker, ReviewerSelect, TypesInput, fromGib, gibLabel, namingRuleMatches, reviewerLabel, toGib } from './shared';

type ExtraKind = Exclude<ItemKind, 'media'>;
const KIND_LABEL: Record<ExtraKind, string> = { document: 'Document', subtitles: 'Subtitles', image: 'Image', audio: 'Audio', other: 'Other' };

export function PortalSpecs() {
  const specs = useDeliverySpecs();
  const choices = usePortalChoices().data;
  const settings = usePortalSettings().data;
  const refresh = usePortalAdminRefresh();
  const [editing, setEditing] = useState<DeliverySpecDTO | 'new' | null>(null);
  const items = specs.data?.items ?? [];
  const qcName = (id: string | null) => (id ? choices?.qcProfiles.find((p) => p.id === id)?.name ?? 'Archived profile' : 'No QC');
  const remove = async (s: DeliverySpecDTO) => {
    if (!window.confirm(`Delete the delivery spec “${s.name}”?`)) return;
    try {
      await api(`/api/v1/admin/portal/specs/${s.id}`, { method: 'DELETE' });
      await refresh();
      toast(`${s.name} deleted`);
    } catch (err) {
      toast(errorDetail(err), 'error');
    }
  };
  const toggle = async (s: DeliverySpecDTO) => {
    try {
      await api(`/api/v1/admin/portal/specs/${s.id}`, { method: 'PATCH', json: { disabled: !s.disabled } });
      await refresh();
    } catch (err) {
      toast(errorDetail(err), 'error');
    }
  };
  return (
    <section className="panel-card" data-testid="portal-specs">
      <div className="portal-card-head">
        <h3><ClipboardList size={16} color="var(--accent-strong)" /> Delivery specs</h3>
        <button className="btn sm" onClick={() => setEditing('new')} data-testid="portal-add-spec"><Plus size={14} /> New spec</button>
      </div>
      <p className="muted portal-lede">The house formats. An assignment for delivery uses one: its QC profile checks the media, and its requested items and metadata are what the supplier is asked for.</p>
      <TableScroll>
        <table className="table compact">
          <thead><tr><th>Name</th><th>QC profile</th><th>Files</th><th>Extra items</th><th>Acceptance</th><th>Reviewer</th><th>In use</th><th /></tr></thead>
          <tbody>
            {specs.data && !items.length && <tr><td colSpan={8} className="muted">No delivery specs yet.</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className={s.disabled ? 'faint' : undefined} data-spec={s.name}>
                <td className="wrap" style={{ minWidth: 150 }}><div style={{ fontWeight: 560 }}>{s.name}</div>{s.disabled && <span className="pill">Disabled</span>}</td>
                <td className="wrap">{qcName(s.qcProfileId)}</td>
                <td className="wrap" style={{ minWidth: 120 }}>{s.acceptedTypes.length ? s.acceptedTypes.join(', ') : 'Any type'}<div className="faint">{gibLabel(s.maxBytes)}</div></td>
                <td className="nowrap">{s.defaultItems.length || '—'}</td>
                <td className="wrap">{s.acceptance ? ACCEPTANCE_LABEL[s.acceptance] : `Portal default (${settings ? ACCEPTANCE_LABEL[settings.defaultAcceptance] : '…'})`}</td>
                <td className="wrap">{reviewerLabel(s.reviewer)}</td>
                <td className="nowrap">{s.activeAssignments ? `${s.activeAssignments} open` : '—'}</td>
                <td className="actions"><div>
                  <button className="btn ghost sm" onClick={() => setEditing(s)} data-testid="portal-edit-spec">Edit</button>
                  <button className="btn ghost sm" onClick={() => void toggle(s)}>{s.disabled ? 'Enable' : 'Disable'}</button>
                  <button className="btn ghost sm icon" aria-label={`Delete ${s.name}`} title={s.activeAssignments ? 'In use by open assignments: disable it instead' : 'Delete'} onClick={() => void remove(s)}><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      {editing && <SpecModal spec={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

function SpecModal({ spec, onClose }: { spec: DeliverySpecDTO | null; onClose: () => void }) {
  const choices = usePortalChoices().data;
  const refresh = usePortalAdminRefresh();
  const [v, setV] = useState({
    name: spec?.name ?? '', description: spec?.description ?? '', qcProfileId: spec?.qcProfileId ?? '', acceptedTypes: spec?.acceptedTypes ?? [],
    maxGib: toGib(spec?.maxBytes), supplierFields: [...new Set([...(spec?.supplierFields ?? []), ...(spec?.requiredFields ?? [])])], requiredFields: spec?.requiredFields ?? [], namingRule: spec?.namingRule ?? '',
    acceptance: (spec?.acceptance ?? '') as AcceptanceMode | '', reviewer: (spec?.reviewer ?? null) as ReviewerRef | null,
    defaultItems: spec?.defaultItems ?? [] as DeliveryItemTemplate[],
  });
  const [example, setExample] = useState('');
  const [busy, setBusy] = useState(false);
  const setItem = (i: number, patch: Partial<DeliveryItemTemplate>) => setV({ ...v, defaultItems: v.defaultItems.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  async function save() {
    setBusy(true);
    try {
      const body = {
        name: v.name.trim(), description: v.description, qcProfileId: v.qcProfileId || null, acceptedTypes: v.acceptedTypes, maxBytes: fromGib(v.maxGib),
        supplierFields: v.supplierFields, requiredFields: v.requiredFields, namingRule: v.namingRule.trim() || null, acceptance: v.acceptance || null, reviewer: v.reviewer,
        defaultItems: v.defaultItems.map((it) => ({ ...it, label: it.label.trim() })),
      };
      await api(spec ? `/api/v1/admin/portal/specs/${spec.id}` : '/api/v1/admin/portal/specs', { method: spec ? 'PATCH' : 'POST', json: body });
      await refresh();
      toast(spec ? 'Delivery spec saved' : `${body.name} added`);
      onClose();
    } catch (err) {
      toast(errorDetail(err), 'error');
      setBusy(false);
    }
  }
  const matches = example ? namingRuleMatches(v.namingRule, example, 'ASSET_2026_00000123') : null;
  return (
    <Modal wide title={spec ? `Edit ${spec.name}` : 'New delivery spec'} onClose={onClose} actions={<>
      <button className="btn" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !v.name.trim() || v.defaultItems.some((it) => !it.label.trim())} onClick={() => void save()} data-testid="portal-spec-save">{spec ? 'Save' : 'Add spec'}</button>
    </>}>
      <div className="field-row">
        <div className="field"><label htmlFor="ds-name">Name</label><input id="ds-name" className="input" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="portal-spec-name" /></div>
        <div className="field">
          <label htmlFor="ds-qc">QC profile</label>
          <select id="ds-qc" className="select" value={v.qcProfileId} onChange={(e) => setV({ ...v, qcProfileId: e.target.value })} data-testid="portal-spec-qc">
            <option value="">No automated QC</option>
            {choices?.qcProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>
      <div className="field"><label htmlFor="ds-desc">Description</label><textarea id="ds-desc" className="textarea" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} placeholder="Shown to suppliers with the assignment" /></div>
      <div className="field-row">
        <div className="field"><label htmlFor="ds-types">Accepted file types</label><TypesInput id="ds-types" value={v.acceptedTypes} onChange={(acceptedTypes) => setV({ ...v, acceptedTypes })} testId="portal-spec-types" /><span className="help">Extensions, e.g. mxf, mov. Empty: any type PRISM ingests.</span></div>
        <div className="field"><label htmlFor="ds-max">Largest file (GiB)</label><input id="ds-max" className="input" inputMode="decimal" value={v.maxGib} placeholder={`Portal maximum`} onChange={(e) => setV({ ...v, maxGib: e.target.value })} /></div>
      </div>

      <div className="field">
        <label>Metadata the supplier fills in</label>
        {/* A field ticked here starts as required; each can be made optional. */}
        <FieldPicker choices={choices} chosen={v.supplierFields}
          onChosen={(supplierFields) => setV({ ...v, supplierFields, requiredFields: [...v.requiredFields.filter((f) => supplierFields.includes(f)), ...supplierFields.filter((f) => !v.supplierFields.includes(f))] })}
          required={v.requiredFields} onRequired={(requiredFields) => setV({ ...v, requiredFields })} testId="portal-spec-fields" />
      </div>

      <div className="field">
        <label>Extra requested items</label>
        <span className="help">Besides the media, every assignment with this spec asks for these (they can be changed per assignment).</span>
        {v.defaultItems.map((it, i) => (
          <div key={i} className="portal-item-row" data-testid="portal-spec-item">
            <input className="input" aria-label="Item name" placeholder="Final manuscript" value={it.label} onChange={(e) => setItem(i, { label: e.target.value })} />
            <select className="select" aria-label="Kind" value={it.kind} onChange={(e) => {
              const kind = e.target.value as ExtraKind;
              setItem(i, { kind, acceptedTypes: [...ITEM_TYPE_PRESETS[ITEM_KIND_PRESET[kind]]] });
            }}>
              {ITEM_KINDS.filter((k): k is ExtraKind => k !== 'media').map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
            <input className="input" aria-label="Description" placeholder="PDF, the version that was shot" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} />
            <TypesInput id={`ds-item-${i}`} value={it.acceptedTypes} onChange={(acceptedTypes) => setItem(i, { acceptedTypes })} placeholder="Any type" />
            <label className="toggle-row portal-item-required"><input type="checkbox" checked={it.required} onChange={(e) => setItem(i, { required: e.target.checked })} /> Required</label>
            <button className="btn ghost sm icon" aria-label="Remove the item" onClick={() => setV({ ...v, defaultItems: v.defaultItems.filter((_, j) => j !== i) })}><Trash2 size={14} /></button>
          </div>
        ))}
        <div><button className="btn ghost sm" disabled={v.defaultItems.length >= 30} onClick={() => setV({ ...v, defaultItems: [...v.defaultItems, { kind: 'document', label: '', description: '', acceptedTypes: [...ITEM_TYPE_PRESETS.document], required: true }] })} data-testid="portal-spec-add-item"><Plus size={14} /> Add an item</button></div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="ds-naming">Naming rule</label>
          <input id="ds-naming" className="input mono" value={v.namingRule} placeholder="Any name" onChange={(e) => setV({ ...v, namingRule: e.target.value })} data-testid="portal-spec-naming" />
          <span className="help">* is any run of characters, ? one character, {'{code}'} the placeholder’s asset code. Not case-sensitive. Example: {'{code}'}_*.mxf</span>
        </div>
        <div className="field">
          <label htmlFor="ds-example">Try a file name</label>
          <input id="ds-example" className="input mono" value={example} placeholder="ASSET_2026_00000123_master.mxf" onChange={(e) => setExample(e.target.value)} />
          {matches !== null && <span className={matches ? 'help portal-ok' : 'help portal-bad'} data-testid="portal-spec-naming-result">{matches ? 'Matches the rule' : 'Does not match'} (with the code ASSET_2026_00000123)</span>}
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="ds-accept">Acceptance</label>
          <select id="ds-accept" className="select" value={v.acceptance} onChange={(e) => setV({ ...v, acceptance: e.target.value as AcceptanceMode | '' })} data-testid="portal-spec-acceptance">
            <option value="">The portal’s default</option>
            <AcceptanceOptions />
          </select>
          <span className="help">Automatic modes act when the spec’s QC profile finishes on the media; the delivery reaches the Library once its required items are accepted too.</span>
        </div>
        <div className="field">
          <label htmlFor="ds-reviewer">Reviewer</label>
          <ReviewerSelect id="ds-reviewer" value={v.reviewer} onChange={(reviewer) => setV({ ...v, reviewer })} choices={choices} testId="portal-spec-reviewer" />
          <span className="help">Deliveries with this spec are assigned to them in Intake.</span>
        </div>
      </div>
    </Modal>
  );
}
